import { Box, useTheme } from '@mui/material';
import React, { ComponentProps, FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as MyFeedIcon } from '../../../assets/images/my_feed.svg';
import { ReactComponent as RecentIcon } from '../../../assets/images/recent.svg';
import { ReactComponent as TodoIcon } from '../../../assets/images/todo.svg';
import { ReactComponent as TrainingIcon } from '../../../assets/images/training_tab.svg';
import { GroupTag } from '../../../state/ducks/auth/types';
import { dashboardMyFeedsActions, dashboardMyFeedsSelectors } from '../../../state/ducks/dashboard.new/my.feeds';
import { dashboardTodoSelectors } from '../../../state/ducks/dashboard.new/todo';
import Text from '../../components/Text';
import useGetHasTag from '../../hooks/useGetHasTag';
import { Translation } from '../../translations/types';
import MyFeeds from '../my.feeds/MyFeeds.container';
import Todo from '../my.feeds/Todo.container';
import Recents from '../recents/Recents.container';
import Trainings from '../trainings/Trainings.container';
import StyledTab from './common/StyledTab';
import StyledTabs from './common/StyledTabs';
import { TabIconWrapper, TabItemCount, TabsContainer, TabsRoot, TabTitle } from './Tabs.styles';
import { DashboardTabValues } from './Tabs.types';

type TabProps = ComponentProps<typeof StyledTab> & {
  title: Translation
  count?: number
  selected?: boolean
};

const Tab: FC<TabProps> = (props) => {
  const { value, selected, icon, title, count, ...restProps } = props;

  return (
    <StyledTab
      value={value}
      disableRipple
      id={`scrollable-auto-tab-${value}`}
      aria-controls={`scrollable-auto-tabpanel-${value}`}
      label={
        <Box display="flex" alignItems="center">
          <TabIconWrapper selected={selected}>{icon}</TabIconWrapper>
          <TabTitle selected={selected}>
            <Text translation={title} />
          </TabTitle>
          {count !== undefined && <TabItemCount selected={selected}>({count})</TabItemCount>}
        </Box>
      }
      {...restProps}
    />
  );
};

const Tabs: FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const isDashboardAdmin = useGetHasTag(GroupTag.DASHBOARD_ADMIN);
  const todoItems: any = useSelector(dashboardTodoSelectors.myFeedsList);
  const feedItems: any = useSelector(dashboardMyFeedsSelectors.myFeedsList);

  useEffect(() => {
    if (!feedItems.total) {
      dispatch(dashboardMyFeedsActions.loadMyFeedsList());
    }
  }, [dispatch, feedItems.total]);

  const [value, setValue] = useState<DashboardTabValues>(DashboardTabValues.Todo);

  const handleChange = useCallback((_: any, newValue: DashboardTabValues) => setValue(newValue), []);

  return (
    <TabsRoot>
      <StyledTabs value={value} onChange={handleChange} aria-label="dashboard tabs">
        <Tab
          value={DashboardTabValues.Todo}
          icon={<TodoIcon />}
          title="common.todo"
          count={todoItems.total ?? 0}
          selected={value === DashboardTabValues.Todo}
        />
        <Tab
          value={DashboardTabValues.MyFeed}
          icon={<MyFeedIcon />}
          title="common.my.feed"
          count={feedItems.total ?? 0}
          selected={value === DashboardTabValues.MyFeed}
        />
        {isDashboardAdmin && (
          <Tab
            sx={{ [theme.breakpoints.down('sm')]: { display: 'none' } }}
            value={DashboardTabValues.Training}
            icon={<TrainingIcon />}
            title="common.trainings"
            selected={value === DashboardTabValues.Training}
          />
        )}
        <Tab
          sx={{ [theme.breakpoints.down('sm')]: { display: 'none' } }}
          value={DashboardTabValues.Recent}
          icon={<RecentIcon />}
          title="common.recent"
          selected={value === DashboardTabValues.Recent}
        />
      </StyledTabs>

      <TabsContainer>
        <div
          role="tabpanel"
          id={`scrollable-auto-tabpanel-${value}`}
          aria-labelledby={`scrollable-auto-tab-${value}`}
        >
          {value === DashboardTabValues.Todo && <Todo />}
          {value === DashboardTabValues.MyFeed && <MyFeeds />}
          {value === DashboardTabValues.Training && <Trainings />}
          {value === DashboardTabValues.Recent && <Recents />}
        </div>
      </TabsContainer>
    </TabsRoot>
  );
};

export default Tabs;
